import { useState, useEffect, useCallback, useMemo } from 'react';
import localApiAdapter from '../../core/services/localApiAdapter';
import {
  buildCrmClientPartySelectOptions,
  buildCrmContactIdSelectOptions,
  buildCrmSalesAgentSelectOptions,
  buildFacturationClientSelectOptions,
} from '../../shared/utils/crmCrossModuleUtils';
import {
  CRM_CAMPAIGNS_REFRESH_EVENT,
  CRM_CONTACTS_REFRESH_EVENT,
  CRM_SALES_AGENTS_REFRESH_EVENT,
  FACTURATION_CLIENTS_REFRESH_EVENT,
} from './crmRefreshEvents';

function unwrapList(response) {
  return Array.isArray(response) ? response : (response?.data || []);
}

function useCrmApiRefreshListener(eventNames, onRefresh) {
  useEffect(() => {
    const names = (Array.isArray(eventNames) ? eventNames : [eventNames]).filter(Boolean);
    if (!names.length || typeof onRefresh !== 'function') return undefined;
    const handler = () => onRefresh();
    names.forEach((name) => window.addEventListener(name, handler));
    return () => names.forEach((name) => window.removeEventListener(name, handler));
  }, [eventNames, onRefresh]);
}

/** Live CRM contacts for pipeline/opportunities/interactions pickers. */
export function useCrmContactsPicker() {
  const [contacts, setContacts] = useState([]);

  const reload = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/crm/contacts');
      setContacts(unwrapList(response));
    } catch {
      setContacts([]);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useCrmApiRefreshListener([CRM_CONTACTS_REFRESH_EVENT, FACTURATION_CLIENTS_REFRESH_EVENT], reload);

  const contactOptions = useMemo(
    () => buildCrmContactIdSelectOptions(contacts),
    [contacts],
  );

  const contactPhoneMap = useMemo(() => {
    const map = {};
    contacts.forEach((c) => {
      map[c.id] = c.phone || c.mobile || '';
    });
    return map;
  }, [contacts]);

  return { contacts, contactOptions, contactPhoneMap, reloadContacts: reload };
}

/** Live sales agents for goals/interactions assignment pickers. */
export function useCrmSalesAgentsPicker() {
  const [agents, setAgents] = useState([]);

  const reload = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/crm/sales-agents');
      setAgents(unwrapList(response));
    } catch {
      setAgents([]);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useCrmApiRefreshListener(CRM_SALES_AGENTS_REFRESH_EVENT, reload);

  const agentOptions = useMemo(
    () => buildCrmSalesAgentSelectOptions(agents),
    [agents],
  );

  return { agents, agentOptions, reloadAgents: reload };
}

/** Live facturation clients — refreshes when billing clients change. */
export function useFacturationClientsPicker() {
  const [clients, setClients] = useState([]);

  const reload = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/facturation/clients');
      const raw = unwrapList(response);
      const rows = Array.isArray(raw) ? raw : (response?.clients || []);
      setClients(rows);
    } catch {
      setClients([]);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useCrmApiRefreshListener(FACTURATION_CLIENTS_REFRESH_EVENT, reload);

  const clientOptions = useMemo(
    () => buildFacturationClientSelectOptions(clients),
    [clients],
  );

  return { clients, clientOptions, reloadClients: reload };
}

/** Live CRM campaigns for lead/interaction assignment pickers. */
export function useCrmCampaignsPicker() {
  const [campaigns, setCampaigns] = useState([]);

  const reload = useCallback(async () => {
    try {
      const response = await localApiAdapter.get('/crm/campaigns');
      setCampaigns(unwrapList(response));
    } catch {
      setCampaigns([]);
    }
  }, []);

  useEffect(() => { void reload(); }, [reload]);
  useCrmApiRefreshListener(CRM_CAMPAIGNS_REFRESH_EVENT, reload);

  const campaignOptions = useMemo(
    () => campaigns.map((c) => ({
      value: String(c.id),
      label: c.name || c.campaign_ref || `#${c.id}`,
    })),
    [campaigns],
  );

  return { campaigns, campaignOptions, reloadCampaigns: reload };
}

/** Merged facturation clients + CRM contacts with cross-module refresh. */
export function useCrmClientPartyPicker() {
  const { contacts, contactOptions, reloadContacts } = useCrmContactsPicker();
  const { clients, clientOptions, reloadClients } = useFacturationClientsPicker();

  const partyOptions = useMemo(
    () => buildCrmClientPartySelectOptions(clients, contacts),
    [clients, contacts],
  );

  return {
    contacts,
    clients,
    contactOptions,
    clientOptions,
    partyOptions,
    reloadParties: useCallback(() => {
      void reloadContacts();
      void reloadClients();
    }, [reloadContacts, reloadClients]),
  };
}

export { useCrmApiRefreshListener };
